import { useCallback, useMemo } from 'react';
import {
  SliderWrapper,
  SliderRow,
  MinMaxLabel,
  RangeTrackWrapper,
  StyledRange,
  TickMark,
  TickLabel,
  ValueDisplay,
  ValueCenter,
  DisabledMessage,
} from './InputSlider.styled';

const InputSlider = ({
  min = 0,
  max = 100,
  step = 1,
  steps,
  value,
  onChange,
  showMinMax = false,
  valueDisplay,
  prefix = '',
  suffix = '',
  trackColor,
  filledTrack = false,
  filledTrackColor,
  tickMark,
  precision,
  thumbPlainPrefix = false,
  thumbPlainSuffix = false,
  hideSlider = false,
  disabled = false,
  disabledMessage,
}) => {
  const resolvedFilledTrackColor = filledTrackColor || trackColor;
  const isStepsMode = Array.isArray(steps) && steps.length > 0;

  const {
    sliderMin,
    sliderMax,
    sliderStep,
    sliderValue,
    displayMin,
    displayMax,
  } = useMemo(() => {
    if (!isStepsMode) {
      return {
        sliderMin: min,
        sliderMax: max,
        sliderStep: step,
        sliderValue: value ?? min,
        displayMin: min,
        displayMax: max,
      };
    }

    const safeValue = value ?? steps[0];
    let closestIndex = 0;
    let closestDiff = Math.abs(steps[0] - safeValue);
    for (let i = 1; i < steps.length; i++) {
      const diff = Math.abs(steps[i] - safeValue);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }

    return {
      sliderMin: 0,
      sliderMax: steps.length - 1,
      sliderStep: 1,
      sliderValue: closestIndex,
      displayMin: steps[0],
      displayMax: steps[steps.length - 1],
    };
  }, [isStepsMode, steps, min, max, step, value]);

  const handleChange = useCallback(
    (e) => {
      if (!isStepsMode) {
        onChange(e);
        return;
      }

      const index = Number(e.target.value);
      const realValue = steps[index];
      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: String(realValue) },
      };
      onChange(syntheticEvent);
    },
    [isStepsMode, steps, onChange],
  );

  const formatValue = useCallback(
    (val, { plainPrefix = false, plainSuffix = false } = {}) => {
      const display = precision != null ? Number(val).toFixed(precision) : val;
      const p = plainPrefix ? '' : prefix;
      const s = plainSuffix ? '' : suffix;
      return `${p}${display}${s}`;
    },
    [prefix, suffix, precision],
  );

  const calcTrackingPercent = useCallback(() => {
    const range = sliderMax - sliderMin;
    if (range === 0) return 50;
    const safeSliderValue = sliderValue ?? sliderMin;
    return ((safeSliderValue - sliderMin) / range) * 100;
  }, [sliderValue, sliderMin, sliderMax]);

  const tickVisible = useMemo(() => {
    if (tickMark == null) return false;
    if (isStepsMode) return steps.indexOf(tickMark) !== -1;
    return tickMark >= min && tickMark <= max;
  }, [tickMark, isStepsMode, steps, min, max]);

  const tickPercent = useMemo(() => {
    if (tickMark == null) return 0;
    if (isStepsMode) {
      const idx = steps.indexOf(tickMark);
      return idx === -1 ? 0 : (idx / (steps.length - 1)) * 100;
    }
    return ((tickMark - min) / (max - min)) * 100;
  }, [tickMark, isStepsMode, steps, min, max]);

  const isTracking =
    valueDisplay === 'tracking-below' || valueDisplay === 'tracking-above';

  const minMaxPosition =
    valueDisplay === 'tracking-below' || valueDisplay === 'below-center'
      ? 'above'
      : 'below';

  const tickLabelPosition =
    valueDisplay === 'tracking-below' || valueDisplay === 'below-center'
      ? 'above'
      : 'below';

  return (
    <>
      {!hideSlider && (
        <SliderWrapper
          $valueDisplay={valueDisplay}
          $showMinMax={showMinMax}
          $minMaxPosition={minMaxPosition}
        >
          <SliderRow>
            <RangeTrackWrapper>
              <StyledRange
                min={sliderMin}
                max={sliderMax}
                step={sliderStep}
                value={sliderValue}
                onChange={handleChange}
                $trackColor={trackColor}
                $filledTrack={filledTrack}
                $filledTrackColor={resolvedFilledTrackColor}
                disabled={disabled}
              />

              {tickVisible && (
                <>
                  <TickMark $percent={tickPercent} />
                  <TickLabel
                    $percent={tickPercent}
                    $position={tickLabelPosition}
                  >
                    {formatValue(tickMark)}
                  </TickLabel>
                </>
              )}

              {showMinMax && (
                <>
                  <MinMaxLabel
                    $position={minMaxPosition}
                    $align="left"
                    $tickVisible={tickVisible}
                  >
                    {formatValue(displayMin)}
                  </MinMaxLabel>
                  <MinMaxLabel
                    $position={minMaxPosition}
                    $align="right"
                    $tickVisible={tickVisible}
                  >
                    {formatValue(displayMax)}
                  </MinMaxLabel>
                </>
              )}

              {isTracking && (
                <ValueDisplay
                  $mode={valueDisplay}
                  $trackingLeft={disabled ? 0 : calcTrackingPercent()}
                  $trackColor={trackColor}
                >
                  {formatValue(value, {
                    plainPrefix: thumbPlainPrefix,
                    plainSuffix: thumbPlainSuffix,
                  })}
                </ValueDisplay>
              )}
            </RangeTrackWrapper>
          </SliderRow>

          {valueDisplay === 'below-center' && (
            <ValueCenter>
              <ValueDisplay $mode="below-center" $trackColor={trackColor}>
                {formatValue(value, {
                  plainPrefix: thumbPlainPrefix,
                  plainSuffix: thumbPlainSuffix,
                })}
              </ValueDisplay>
            </ValueCenter>
          )}
        </SliderWrapper>
      )}

      {disabled && disabledMessage && (
        <DisabledMessage $hideSlider={hideSlider}>{disabledMessage}</DisabledMessage>
      )}
    </>
  );
};

export default InputSlider;
